import React from 'react';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';

/**
 * Catches render-time exceptions so one broken page cannot take the app down.
 *
 * Without a boundary React 18 unmounts the entire tree on an uncaught render throw,
 * which reaches the user as a blank white page with nothing to click. Mounted around
 * the routed <Outlet /> in Layout, so the header and sidebar survive and the user can
 * navigate away from the page that failed.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Render error caught by ErrorBoundary:', error, errorInfo);
  }

  render() {
    const { error } = this.state;

    if (!error) {
      return this.props.children;
    }

    return (
      <Box sx={{ p: 2 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Reload
            </Button>
          }
        >
          <AlertTitle>This page ran into a problem</AlertTitle>
          Something on this page failed to display. The rest of the app still works — pick another
          page from the menu, or reload to try again.
          <Typography variant="caption" component="div" sx={{ mt: 1, opacity: 0.8 }}>
            {error?.message || String(error)}
          </Typography>
        </Alert>
      </Box>
    );
  }
}

export default ErrorBoundary;
